import { Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface SocialMediaLikeButtonProps {
  count?: number;
  isLiked?: boolean;
  onLike?: () => void;
  disabled?: boolean;
}

const SocialMediaLikeButton = ({ 
  count = 0, 
  isLiked = false, 
  onLike, 
  disabled = true // Disabled for now as like functionality isn't implemented yet
}: SocialMediaLikeButtonProps) => {
  const [localLiked, setLocalLiked] = useState(isLiked);
  const [localCount, setLocalCount] = useState(count);

  const formatCount = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return num.toString();
  };

  const handleLike = () => {
    if (disabled) return;
    
    const newLiked = !localLiked;
    setLocalLiked(newLiked);
    setLocalCount(prev => newLiked ? prev + 1 : Math.max(0, prev - 1));
    
    onLike?.();
  };

  return (
    <motion.button
      onClick={handleLike}
      disabled={disabled}
      className={`flex items-center space-x-1 px-3 py-1.5 rounded-full transition-all duration-200 ${
        disabled 
          ? 'cursor-default opacity-70' 
          : 'hover:bg-red-50 active:scale-95'
      }`}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
    >
      <motion.div
        animate={localLiked ? { scale: [1, 1.2, 1] } : {}}
        transition={{ duration: 0.3 }}
      >
        <Heart 
          className={`w-5 h-5 transition-colors ${
            localLiked 
              ? 'fill-red-500 text-red-500' 
              : 'text-gray-400 hover:text-red-400'
          }`}
        />
      </motion.div>
      <span className={`text-sm font-medium ${
        localLiked ? 'text-red-500' : 'text-gray-600'
      }`}>
        {formatCount(localCount)}
      </span>
    </motion.button>
  );
};

export default SocialMediaLikeButton;